import React, { useState, useEffect, useContext } from "react";
import "./styles.scss";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import DataTable from "react-data-table-component";
import HeaderInside from "../../../Components/HeaderInside";
import MainContext from "../../../Contexts/MainContext";
import { showToast, checkIfAdmin, checkIfGestor } from "../../../Functions";
import api from "../../../Services/api";
import { ALL_PONTO_COLABORADOR } from "../../../Services/Endpoints";
import { ColumsTablePontos } from "../../../Services/TableColumns";
import EmptyData from "../../../Components/EmptyData";
import Lottie from "react-lottie";
import GeneratePDF from "../../../Components/GeneratePDF";
import { GetAllColaboradores } from "../../../Services/ApiCalls";

interface RegistrosProps {
    dataPonto: any;
}

const Registros: React.FC<RegistrosProps> = ({ dataPonto }) => {
    const { token } = useContext(MainContext);
    const LOADING = require("../../../Assets/animations/loading.json");

    // Identificação do usuário logado e perfis
    const loggedUserId = dataPonto.colaboradorId;
    const isAdmin = checkIfAdmin(dataPonto.perfis);
    const isGestor = checkIfGestor(dataPonto.perfis);
    const canSearchOthers = isAdmin || isGestor;

    // Estados de busca
    const [selectedColaboradorId, setSelectedColaboradorId] = useState<string | number>(loggedUserId);
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    // Estados de dados
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [pontos, setPontos] = useState<any[]>([]);

    // Carregar colaboradores se for admin/gestor
    const { dataAllColaboradores } = GetAllColaboradores(token, false);

    useEffect(() => {
        document.title = "Marca Ponto - Registros";
        // Define as datas iniciais como o mês atual (do dia 1 ao último dia)
        setEsteMes();
    }, []);

    // Converte string "DD/MM/YYYY" para Date
    const parseDateStr = (dateStr: string) => {
        if(!dateStr) return new Date(0);
        // Algumas datas podem estar em "YYYY-MM-DD", mas o banco original tem "18/05/2026"
        if(dateStr.includes("/")) {
            const [d, m, y] = dateStr.split("/");
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0); // 12:00 evita timezone bugs
        }
        if(dateStr.includes("-")) {
            const [y, m, d] = dateStr.split("-");
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
        }
        return new Date(0);
    };

    const handleSearch = async () => {
        if(!startDate || !endDate) {
            showToast("ERROR", "Selecione a data de início e fim", {});
            return;
        }

        setIsFetchingData(true);
        try {
            const resp = await api.get(`${ALL_PONTO_COLABORADOR}/${selectedColaboradorId}`, {
                headers: { Authorization: token },
            });

            if (resp.status === 200 && resp.data) {
                // Filtrar os pontos entre startDate e endDate
                // Zerar as horas para comparar só os dias
                const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);

                const filtered = resp.data.filter((p: any) => {
                    const pDate = parseDateStr(p.data);
                    return pDate >= start && pDate <= end;
                });

                setPontos(filtered);
            } else {
                setPontos([]);
            }
        } catch (err) {
            showToast("ERROR", "Erro ao buscar registros", {});
            setPontos([]);
        } finally {
            setIsFetchingData(false);
        }
    };

    // Funções de atalho
    const setEstaSemana = () => {
        const curr = new Date();
        const first = curr.getDate() - curr.getDay(); 
        const last = first + 6;
        setStartDate(new Date(curr.setDate(first)));
        setEndDate(new Date(curr.setDate(last)));
    };

    const setEsteMes = () => {
        const date = new Date();
        setStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
        setEndDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    };

    const setMesAnterior = () => {
        const date = new Date();
        setStartDate(new Date(date.getFullYear(), date.getMonth() - 1, 1));
        setEndDate(new Date(date.getFullYear(), date.getMonth(), 0));
    };

    const setEsteAno = () => {
        const date = new Date();
        setStartDate(new Date(date.getFullYear(), 0, 1));
        setEndDate(new Date(date.getFullYear(), 11, 31));
    };

    return (
        <div className="registros__wrapper pontos__wrapper">
            <div className="registros__header pontos__header">
                <HeaderInside isHome={false} nome={"Registros"} />

                <div className="page__title-info info__registros">
                    <div className="tinf__name">
                        <h2 className="tt-title title-blue title-bold">
                            Registros de Ponto
                        </h2>
                    </div>

                    <ul className="registros__filter">
                        {canSearchOthers && (
                            <li>
                                <label>Colaborador</label>
                                <select 
                                    value={selectedColaboradorId} 
                                    onChange={(e) => setSelectedColaboradorId(e.target.value)}
                                >
                                    {dataAllColaboradores.map((colab: any) => (
                                        <option key={colab.id} value={colab.id}>
                                            {colab.nome}
                                        </option>
                                    ))}
                                </select>
                            </li>
                        )}
                        <li>
                            <label>Data Inicial</label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date: Date) => setStartDate(date)}
                                dateFormat="dd/MM/yyyy"
                            />
                        </li>
                        <li>
                            <label>Data Final</label>
                            <DatePicker
                                selected={endDate}
                                onChange={(date: Date) => setEndDate(date)}
                                dateFormat="dd/MM/yyyy"
                            />
                        </li>
                        <li>
                            <button className="bt" onClick={handleSearch}>
                                Buscar
                            </button>
                        </li>
                    </ul>

                    <div className="registros__quick-filters">
                        <button onClick={setEstaSemana}>Esta Semana</button>
                        <button onClick={setEsteMes}>Este Mês</button>
                        <button onClick={setMesAnterior}>Mês Anterior</button>
                        <button onClick={setEsteAno}>Este Ano</button>
                    </div>

                    <div className={`page__download ${pontos.length === 0 ? "is__dropdown" : ""}`}>
                        <a
                            href="#pdf"
                            className={`bt ${pontos.length === 0 ? "not__available" : ""}`}
                            onClick={() => {
                                const PDFOutput = GeneratePDF(
                                    ["ID", "Data", "Horário", "Localização", "Manual?", "Tipo"],
                                    pontos,
                                    "registros_ponto.pdf",
                                    `Registros de Ponto`
                                );
                            }}
                        >
                            Download
                        </a>
                    </div>
                </div>
            </div>

            <div className="table__wrapper">
                {!isFetchingData ? (
                    <DataTable
                        noHeader={true}
                        data={pontos}
                        columns={ColumsTablePontos}
                        noDataComponent={<EmptyData hasMargin={true} />}
                        striped={true}
                        pagination={true}
                        highlightOnHover={true}
                        paginationPerPage={15}
                    />
                ) : (
                    <Lottie
                        options={{
                            loop: true,
                            animationData: LOADING,
                        }}
                        height={200}
                        width={200}
                    />
                )}
            </div>
        </div>
    );
};

export default Registros;
